import { useEffect, useMemo, useRef, useState } from 'react';
import {
	flexRender,
	getCoreRowModel,
	useReactTable,
} from '@tanstack/react-table';

import { SearchableHeader } from './SearchableHeader';
import { Pagination } from './Pagination';
import { ColumnSettingsMenu } from './ColumnSettingsMenu';
import {
	pinStickyRightColumns,
	getStickyRightIdsFromColumns,
	getLeafIdsFromColumns,
} from './pinStickyColumns';

/**
 * SmartTable: tabla controlada construida sobre @tanstack/react-table + Tailwind.
 *
 * Disenada para vivir junto a `useTablePrefs` (preferencias persistidas de forma hibrida:
 * sessionStorage local + backend en /users/:userId/table-prefs/:tableKey).
 *
 * Modo server-side: page/pageSize/sorting se reportan via callbacks; el page contenedor
 * dispara los fetch al cambiar `query`.
 *
 * La tabla crece naturalmente en vertical: no tiene altura maxima ni scroll interno.
 * El scroll horizontal sigue activo si el ancho total supera el contenedor.
 *
 * Props:
 * - data, columns, totalRows, query, view: estado controlado.
 * - onQueryChange(patch), onViewChange(patch): callbacks de cambio.
 * - onColumnSearchChange(columnId, value): cambia el filtro inline por columna.
 * - loading, emptyMessage, pageSizeOptions, headerActions, onResetPrefs, getRowId: opcionales.
 * - showColumnSettings (default true): si es false, oculta el menú de columnas.
 * - fillWidth (default false): la tabla ocupa el 100% del contenedor (columnas crecen).
 * - enableColumnResizing (default true): si es false, oculta el handle de resize.
 * - Con `loading=true` el tbody muestra filas esqueleto (animate-blink) en lugar del spinner.
 * - `preferencesReady` (default true): si es false, cabecera y cuerpo muestran skeleton hasta
 *   que termine la hidratacion remota de preferencias (evita parpadeo de columnas/filtros).
 */
export const SmartTable = ({
	data,
	columns,
	totalRows = 0,
	query,
	onQueryChange,
	view,
	onViewChange,
	loading = false,
	preferencesReady = true,
	emptyMessage = 'No hay registros',
	pageSizeOptions,
	headerActions = null,
	onResetPrefs,
	getRowId,
	onColumnSearchChange = () => {},
	showColumnSettings = true,
	fillWidth = false,
	embedded = false,
	enableColumnResizing = true,
}) => {
	const rows = useMemo(() => Array.isArray(data) ? data : [], [data]);
	const showSkeletonRows = !preferencesReady || loading;
	const showHeaderSkeleton = !preferencesReady;

	const scrollContainerRef = useRef(null);
	const [hasScrolledHorizontally, setHasScrolledHorizontally] = useState(false);

	const leafIds = useMemo(() => getLeafIdsFromColumns(columns), [columns]);
	const stickyRightIds = useMemo(() => getStickyRightIdsFromColumns(columns), [columns]);

	const columnOrder = useMemo(
		() => pinStickyRightColumns(view.columnOrder, leafIds, stickyRightIds),
		[view.columnOrder, leafIds, stickyRightIds],
	);

	const table = useReactTable({
		data: rows,
		columns,
		getRowId: getRowId || ((row, index) => row?.id ?? row?._id ?? String(index)),
		state: {
			pagination: { pageIndex: query.page, pageSize: query.pageSize },
			sorting: query.sorting || [],
			columnVisibility: view.columnVisibility || {},
			columnOrder,
			columnSizing: view.columnSizing || {},
		},
		manualPagination: true,
		manualSorting: true,
		pageCount: Math.max(1, Math.ceil((totalRows || 0) / Math.max(1, query.pageSize))),
		rowCount: totalRows,
		columnResizeMode: 'onChange',
		enableColumnResizing,
		enableSortingRemoval: true,
		getCoreRowModel: getCoreRowModel(),
		onPaginationChange: (updater) => {
			const prev = { pageIndex: query.page, pageSize: query.pageSize };
			const next = typeof updater === 'function' ? updater(prev) : updater;
			onQueryChange({ page: next.pageIndex, pageSize: next.pageSize });
		},
		onSortingChange: (updater) => {
			const prev = query.sorting || [];
			const next = typeof updater === 'function' ? updater(prev) : updater;
			onQueryChange({ sorting: next, page: 0 });
		},
		onColumnVisibilityChange: (updater) => {
			const prev = view.columnVisibility || {};
			const next = typeof updater === 'function' ? updater(prev) : updater;
			onViewChange({ columnVisibility: next });
		},
		onColumnOrderChange: (updater) => {
			const prev = columnOrder;
			const next = typeof updater === 'function' ? updater(prev) : updater;
			onViewChange({
				columnOrder: pinStickyRightColumns(next, leafIds, stickyRightIds),
			});
		},
		onColumnSizingChange: (updater) => {
			const prev = view.columnSizing || {};
			const next = typeof updater === 'function' ? updater(prev) : updater;
			onViewChange({ columnSizing: next });
		},
	});

	useEffect(() => {
		const el = scrollContainerRef.current;
		if (!el) return undefined;
		const measure = () => {
			setHasScrolledHorizontally(el.scrollLeft > 0);
		};
		measure();
		el.addEventListener('scroll', measure, { passive: true });
		return () => {
			el.removeEventListener('scroll', measure);
		};
	}, []);

	return (
		<div className={embedded
			? 'isolate flex h-full w-full min-w-0 flex-col overflow-hidden rounded-xl border border-slate-200 bg-white'
			: 'isolate flex h-full w-full min-w-0 flex-col rounded-xl border border-slate-200 bg-white shadow-sm'
		}>
			{(headerActions || showColumnSettings) ? (
				<div className='flex flex-wrap items-center justify-end gap-2 border-b border-gray-100 px-3 py-2'>
					{headerActions}
					{showColumnSettings ? (
						<div className='flex w-full justify-end md:contents'>
							<ColumnSettingsMenu table={table} onReset={onResetPrefs} disabled={!preferencesReady} />
						</div>
					) : null}
				</div>
			) : null}

			<div
				ref={scrollContainerRef}
				className='relative min-h-0 w-full flex-1 overflow-x-auto'
				style={{ '--sticky-shadow-opacity': hasScrolledHorizontally ? 1 : 0 }}
			>
				<table
					className='w-full border-collapse text-sm text-gray-800'
					style={fillWidth
						? { width: '100%', minWidth: table.getCenterTotalSize() }
						: { width: table.getCenterTotalSize() }}
					aria-busy={showSkeletonRows}
				>
					<thead className='bg-slate-50'>
						{table.getHeaderGroups().map((headerGroup) => (
							<tr key={headerGroup.id} className='border-b border-slate-200'>
								{headerGroup.headers.map((header) => (
									<th
										key={header.id}
										style={fillWidth
											? { minWidth: header.getSize() }
											: { width: header.getSize() }}
										className={headerStickyClass(header, 'th')}
									>
										{header.isPlaceholder ? null : showHeaderSkeleton ? (
											<div className='flex h-full min-w-0 items-center py-0.5 pr-6'>
												<div className='h-3.5 w-24 max-w-[85%] min-w-[2rem] rounded-md bg-slate-200/90 animate-blink' />
											</div>
										) : (
											<>
												<SearchableHeader
													header={header}
													value={query.columnSearch?.[header.column.id] || ''}
													onChange={(next) => onColumnSearchChange(header.column.id, next)}
												/>
												{header.column.getCanResize() && <ResizeHandle header={header} />}
											</>
										)}
									</th>
								))}
							</tr>
						))}
					</thead>

					<tbody>
						{showSkeletonRows && (
							<TableSkeletonRows
								columns={table.getVisibleLeafColumns()}
								rowCount={Math.min(Math.max(query.pageSize || 10, 5), 15)}
								fillWidth={fillWidth}
							/>
						)}

						{!showSkeletonRows && rows.length === 0 && (
							<tr>
								<td
									colSpan={table.getVisibleLeafColumns().length}
									className='px-4 py-12 text-center text-sm text-gray-400'
								>
									{emptyMessage}
								</td>
							</tr>
						)}

						{!showSkeletonRows && rows.length > 0 && table.getRowModel().rows.map((row) => (
							<tr
								key={row.id}
								className='group border-b border-gray-100 bg-white transition-colors hover:bg-indigo-50'
							>
								{row.getVisibleCells().map((cell) => (
									<td
										key={cell.id}
										style={fillWidth
											? { minWidth: cell.column.getSize() }
											: { width: cell.column.getSize() }}
										className={cellStickyClass(cell, 'td')}
									>
										{flexRender(cell.column.columnDef.cell, cell.getContext())}
									</td>
								))}
							</tr>
						))}
					</tbody>
				</table>
			</div>

			<Pagination
				page={query.page}
				pageSize={query.pageSize}
				totalRows={totalRows}
				pageSizeOptions={pageSizeOptions}
				disabled={!preferencesReady}
				onPageChange={(nextPage) => onQueryChange({ page: nextPage })}
				onPageSizeChange={(nextSize) => onQueryChange({ pageSize: nextSize, page: 0 })}
			/>
		</div>
	);
};

// Sombra del lado izquierdo de la columna sticky derecha. Solo aparece cuando hay scroll
// horizontal activo (controlado por la CSS var `--sticky-shadow-opacity`).
const stickyShadowOverlay =
	"before:pointer-events-none before:absolute before:inset-y-0 before:-left-3 before:w-3 before:bg-gradient-to-l before:from-slate-900/15 before:to-transparent before:content-[''] before:transition-opacity before:duration-200 before:[opacity:var(--sticky-shadow-opacity,0)]";

const headerStickyClass = (header, role) => {
	const sticky = header.column.columnDef.meta?.sticky === 'right';
	const baseTh =
		'relative h-11 px-4 py-0 text-left align-middle text-[11px] font-semibold uppercase tracking-wider text-slate-500';
	const baseTd = 'relative px-4 py-3 align-middle';
	if (role === 'th') {
		if (!sticky) return baseTh;
		return `${baseTh} sticky right-0 z-[2] bg-slate-50 ${stickyShadowOverlay} before:from-slate-900/20`;
	}
	if (!sticky) return baseTd;
	return `${baseTd} sticky right-0 z-[1] bg-white group-hover:bg-indigo-50 ${stickyShadowOverlay}`;
};

const cellStickyClass = (cell, role) => headerStickyClass({ column: cell.column }, role);

const skeletonWidths = ['w-full', 'w-[88%]', 'w-[72%]', 'w-[92%]', 'w-4/5', 'w-[64%]'];

function TableSkeletonRows({ columns, rowCount, fillWidth = false }) {
	return (
		<>
			{Array.from({ length: rowCount }, (_, rowIdx) => (
				<tr key={`sk-${rowIdx}`} className='border-b border-gray-100 bg-white' aria-hidden='true'>
					{columns.map((column, colIdx) => (
						<td
							key={`${rowIdx}-${column.id}`}
							style={fillWidth
								? { minWidth: column.getSize() }
								: { width: column.getSize() }}
							className={cellStickyClass({ column }, 'td')}
						>
							<div className='flex min-w-0 items-center py-0.5 pr-6'>
								<div
									className={`h-4 max-w-full min-w-[2rem] rounded-md bg-slate-200/90 animate-blink ${skeletonWidths[(rowIdx + colIdx) % skeletonWidths.length]}`}
								/>
							</div>
						</td>
					))}
				</tr>
			))}
		</>
	);
}

/**
 * Handle de resize visible: dos barritas verticales tipo "grip" alineadas al borde derecho del th.
 * Funciona como divisor entre columnas Y como affordance visual de redimensionado:
 *  - estado normal: slate-300 (sutil, descubrible)
 *  - hover: indigo-500 + fondo suave (claramente interactivo)
 *  - resizing: indigo-600 + fondo solido (feedback en vivo)
 * El area clickeable es de 12px (w-3) para facilitar el agarre con mouse y touch.
 * z-0 (no z-10): el navbar fijo usa z-10; mismo valor hacia que los grips pintaran encima
 * de la barra de navegacion por orden en el DOM.
 */
const ResizeHandle = ({ header }) => {
	const isResizing = header.column.getIsResizing();
	const interactive = isResizing
		? 'bg-indigo-500/15 text-indigo-600'
		: 'text-slate-300 hover:bg-indigo-500/10 hover:text-indigo-500';
	return (
		<span
			role='separator'
			aria-orientation='vertical'
			aria-label='Redimensionar columna'
			onMouseDown={header.getResizeHandler()}
			onTouchStart={header.getResizeHandler()}
			onClick={(e) => e.stopPropagation()}
			className={`absolute right-0 top-0 z-0 flex h-full w-3 cursor-col-resize touch-none select-none items-center justify-center transition-colors ${interactive}`}
		>
			<svg width='6' height='14' viewBox='0 0 6 14' fill='currentColor' aria-hidden='true'>
				<rect x='0' y='1' width='1.5' height='12' rx='0.75' />
				<rect x='4.5' y='1' width='1.5' height='12' rx='0.75' />
			</svg>
		</span>
	);
};
